import { Link } from "wouter";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES } from "@/lib/constants";

export default function CaseVolvo() {
  return (
    <Layout>
      <SEOHead
        title="Volvo prototyppartnerskap - Kundcase | ASPAB"
        description="Alla Volvos prototyper och kundanpassningar går genom ASPAB. Läs om hur vi levererar komplexa, unika projekt med hög precision och snabb leverans."
        canonical="https://aspab.com/referenser/volvo"
        path="/referenser/volvo"
        breadcrumbs={[
          { name: "Referenser", href: "/referenser" },
          { name: "Volvo", href: "/referenser/volvo" },
        ]}
      
        ogImage="/images/og-case-volvo_2c97493b.png"
      />

      <PageHero
        title="Volvo prototyppartnerskap"
        subtitle="Alla Volvos prototyper och kundanpassningar går genom ASPAB. Ett partnerskap byggt på precision, flexibilitet och förtroende."
        breadcrumbs={[
          { name: "Referenser", href: "/referenser" },
          { name: "Volvo", href: "/referenser/volvo" },
        ]}
        image={IMAGES.closeup1}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection>
              <div className="flex flex-wrap gap-2 mb-8">
                {["Prototyper", "Fordonsindustri", "Hög precision", "Kundanpassning"].map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded-lg">
                    {tag}
                  </span>
                ))}
              </div>

              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Bakgrund</h2>
              <p className="text-gray-500 mt-4 leading-relaxed">
                Volvo kommer till ASPAB med alla sina prototyper och kundanpassningar. Det handlar 
                om både helt nya fordonskoncept och specialanpassningar för specifika kunder. Varje 
                projekt är unikt och har aldrig gjorts förut, vilket ställer höga krav på teknisk 
                kompetens och flexibilitet.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-12">Utmaningen</h2>
              <p className="text-gray-500 mt-4 leading-relaxed">
                Prototyparbete innebär komplexa, unika projekt som kräver hög precision och snabba 
                leveranstider. Specifikationerna är inte alltid kompletta, ibland kommer Volvo med 
                ofullständiga underlag som kräver problemlösning och konstruktionshjälp.
              </p>
              <div className="mt-6 space-y-3">
                {[
                  "Komplexa, unika projekt som aldrig gjorts förut",
                  "Höga krav på precision och kvalitet",
                  "Snabba leveranstider för prototyputveckling",
                  "Varje projekt kräver individuell problemlösning",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                    <span className="text-gray-500">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={150}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-12">Vår lösning</h2>
              <p className="text-gray-500 mt-4 leading-relaxed">
                Volvo vänder sig kontinuerligt till Aspab för sina prototyper och kundanpassningar – 
                från helt nya fordonskoncept till speciallösningar för enskilda kunder. Vi använder 
                våra moderna CO2-lasrar med toleranser ner till 0,1 mm för perfekt passform.
              </p>

              <h3 className="font-heading text-xl font-semibold text-gray-900 mt-8">Konsultativt arbetssätt med teknisk expertis</h3>
              <p className="text-gray-500 mt-3 leading-relaxed">
                Vår konsultativa approach är central i samarbetet. När Volvo kommer med 
                ofullständiga specifikationer hjälper vi med konstruktion och materialrådgivning 
                för att nå optimal prestanda.
              </p>
              <div className="mt-4 space-y-3">
                {[
                  "Moderna CO2-lasrar med toleranser ner till 0,1 mm för perfekt passform",
                  "Materialrådgivning för att optimera prestanda och kostnad",
                  "Den konsultativa approachen sparar tid, minskar risk för fel och skapar mervärde genom hela utvecklingsprocessen",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                    <span className="text-gray-500">{item}</span>
                  </div>
                ))}
              </div>

              <blockquote className="mt-8 pl-5 border-l-2 border-[#2563EB]/50">
                <p className="text-gray-500 italic leading-relaxed">
                  "Ibland får vi ju en skiss på ett papper, sen ska vi bygga ett flygplan av en skruv. 
                  Alla Volvo prototyper och kundanpassningar kommer ju till oss."
                </p>
                <cite className="text-[#2563EB] text-sm mt-2 block not-italic">Robin Fischer, ASPAB</cite>
              </blockquote>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-12">Resultat</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {[
                  { title: "Etablerat partnerskap", desc: "Volvo förlitar sig på ASPAB för alla prototyparbeten." },
                  { title: "Fortsatt expansion", desc: "Volvo väntar på vår nya rörlaser-kapacitet för framtida projekt." },
                  { title: "Långsiktig relation", desc: "Kontinuerlig affärsrelation som sträcker sig över flera år." },
                  { title: "Tekniskt förtroende", desc: "ASPAB ses som en teknisk partner, inte bara en leverantör." },
                ].map((r) => (
                  <div key={r.title} className="bg-white border border-gray-200 rounded-lg p-5">
                    <h3 className="text-gray-900 font-semibold">{r.title}</h3>
                    <p className="text-gray-500 text-sm mt-1">{r.desc}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            {/* Navigation */}
            <AnimatedSection delay={250}>
              <div className="mt-16 pt-8 border-t border-gray-200 flex flex-col sm:flex-row justify-between gap-4">
                <Link href="/referenser" className="flex items-center gap-2 text-gray-500 hover:text-[#2563EB] transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Alla referenser
                </Link>
                <Link href="/referenser/forsvarsindustrin" className="flex items-center gap-2 text-[#2563EB] hover:gap-3 transition-all">
                  Försvarsindustrin <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </Layout>
  );
}
